/* eslint-disable sort-keys */
import { useBuildVars } from 'src/config';
import {
  ManualTemplates,
  ProgramDetailFieldProperties,
  ProgramDetailLoadResponse,
} from '../../hooks/program-detail-load';
import { ApiError, useApiPost } from 'src/api';
import { useMutation, useQueryClient } from 'react-query';
import { useAccount } from '@azure/msal-react';
import { VendorProgramTemplateData } from './program-template';

export type StatusRequest = { isActive?: boolean; isDraft?: boolean };
export type VendorProgramTemplateRequest = VendorProgramTemplateData &
  StatusRequest;
export interface RequestPreamble extends StatusRequest {
  businessType: string | null;
  createdby: string;
  customergroupinfo: any;
  endUserAddressMatchPercentage: string;
  isExactEnduserNameMatchRequired: string;
  sk_company: number;
  uniqueEnduserResellerForClaims: boolean;
  vendorcode: string;
  vendorid: number;
  vendorname: string;
  vendorprogramsdescription: string;
  vendorprogramsname: string;
}

export interface ProgramDetailCreateRequest {
  manualTemplates: ManualTemplates;
  manualTemplatesFieldProperties: ProgramDetailFieldProperties;
  requestPreamble: RequestPreamble;
}

export interface ProgramDetailCreateResponse {
  message: string;
  responsePreamble: {
    errors: null;
    transactionID: string;
    transactionStatus: string;
  };
  statusCode: number;
  transactionResponse: {
    manualTemplates: null;
    manualTemplatesFieldProperties: null;
    vendorProgramId: string;
  };
  translationKey: null;
}
export function useProgramDetailCreate(vendorName: string, vendorId: number) {
  const buildVars = useBuildVars();
  const apiPost = useApiPost<any>({
    host: buildVars.VP_API_BASE,
  });

  const queryClient = useQueryClient();
  const account = useAccount();

  return useMutation<
    ProgramDetailCreateResponse,
    ApiError | undefined,
    VendorProgramTemplateRequest
  >(
    (req) => {
      const {
        source,
        vendorcode,
        vendorprogramsdescription,
        vendorprogramsname,
        ...manualTemplatesFieldProperties
      } = req;

      const requestPreamble: RequestPreamble = {
        businessType: (req.businessType as string) ?? null,
        createdby: account?.username ?? '',
        customergroupinfo: req.customergroupinfo ?? [],
        endUserAddressMatchPercentage:
          (req.endUserAddressMatchPercentage as string) ?? '',
        isExactEnduserNameMatchRequired:
          (req.isExactEnduserNameMatchRequired as string) ?? '',
        uniqueEnduserResellerForClaims:
          (req.uniqueEnduserResellerForClaims as boolean) ?? false,
        sk_company: 0,
        vendorcode: (req.vendorcode as string) ?? '',
        vendorid: vendorId,
        vendorname: vendorName,
        vendorprogramsdescription:
          (req.vendorprogramsdescription as string) ?? '',
        vendorprogramsname: (req.vendorprogramsname as string) ?? '',
        isActive: req.isActive,
        isDraft: req.isDraft,
      };

      const reqData: ProgramDetailCreateRequest = {
        manualTemplates: { source: (req.source as string) ?? '' },
        manualTemplatesFieldProperties:
          manualTemplatesFieldProperties as ProgramDetailFieldProperties,
        requestPreamble: requestPreamble,
      };

      return apiPost(
        '/system.webapi.gds/api/v1/gdsCoreApiVendor/CreateVendorProgram',
        reqData
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries(
            '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgram'
          )
          .catch(() => undefined);
      },
    }
  );
}

export interface ProgramDetailUpdateRequest {
  manualTemplates: ManualTemplates;
  manualTemplatesFieldProperties: ProgramDetailFieldProperties;
  requestPreamble: Omit<RequestPreamble, 'businessType'> & {
    lastchange_user: string;
    status?: string;
    vendorprogramid: number;
  };
}
export function useProgramDetailUpdate(
  countryCode: string,
  vendorId: number,
  vendorProgramId: number,
  vendorName: string
) {
  const buildVars = useBuildVars();
  const queryClient = useQueryClient();
  const account = useAccount();
  const apiPost = useApiPost<any>({
    host: buildVars.VP_API_BASE,
  });

  return useMutation<any, ApiError | undefined, VendorProgramTemplateRequest>(
    (req) => {
      const programDetail = queryClient.getQueryData([
        '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgramEdit',
        {
          countryCode: countryCode,
          vendorID: vendorId,
          vendorprogramid: vendorProgramId,
        },
        undefined,
      ]) as ProgramDetailLoadResponse;
      const {
        customergroupinfo,
        source,
        vendorcode,
        vendorprogramsdescription,
        vendorprogramsname,
        ...manualTemplatesFieldProperties
      } = req;

      const requestPreamble: ProgramDetailUpdateRequest['requestPreamble'] = {
        createdby: account?.username ?? '',
        customergroupinfo: customergroupinfo ?? [],
        endUserAddressMatchPercentage:
          (req.endUserAddressMatchPercentage as string) ?? '',
        isExactEnduserNameMatchRequired:
          (req.isExactEnduserNameMatchRequired as string) ?? '',
        uniqueEnduserResellerForClaims:
          (req.uniqueEnduserResellerForClaims as boolean) ?? false,
        lastchange_user: account?.username ?? '',
        sk_company: programDetail.vendorProgramDetail?.sk_company ?? 0,
        status: programDetail.vendorProgramDetail?.status,
        vendorcode: (vendorcode as string) ?? '',
        vendorid: vendorId,
        vendorname: programDetail.vendorProgramDetail?.vendorname ?? vendorName,
        vendorprogramid: vendorProgramId,
        vendorprogramsdescription: (vendorprogramsdescription as string) ?? '',
        vendorprogramsname: (vendorprogramsname as string) ?? '',
        isActive: req.isActive,
        isDraft: req.isDraft,
      };

      const reqData: ProgramDetailUpdateRequest = {
        manualTemplates: { source: (source as string) ?? '' },
        manualTemplatesFieldProperties:
          manualTemplatesFieldProperties as ProgramDetailFieldProperties,
        requestPreamble: requestPreamble,
      };

      return apiPost(
        '/system.webapi.gds/api/v1/gdsCoreApiVendor/UpdateVendorProgram',
        reqData
      );
    },
    {
      onSuccess: () => {
        queryClient
          .invalidateQueries(
            '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgramEdit'
          )
          .catch(() => undefined);
        queryClient
          .invalidateQueries(
            '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgram'
          )
          .catch(() => undefined);
      },
    }
  );
}

export interface UpdateProductSkuRequest {
  companyCode: string;
  countryCode: string;
  vendorCodes: string[];
  vendorName?: string;
  vendorProgramName?: string;
}
export interface UpdateProductSkuResponse {
  message?: string;
}

export function useUpdateProductSku() {
  const buildVars = useBuildVars();
  const apiPost = useApiPost({
    host: buildVars.VP_API_BASE,
  });

  return useMutation<
    UpdateProductSkuResponse,
    ApiError | undefined,
    UpdateProductSkuRequest
  >((req) =>
    apiPost(
      '/system.webapi.gds/api/v1/gdsCoreApiExternalApi/ProductRefresh',
      {},
      {
        vendorCode: req.vendorCodes.join(','),
        vendorName: req.vendorName,
        vendorProgramName: req.vendorProgramName,
      },
      {
        'IM-CountryCode': req.countryCode,
        'IM-CompanyCode': req.companyCode,
      }
    )
  );
}
